// src/utils/courseAccess.js
//
// Single source of truth for "which course categories does an
// enrolled track unlock." Both CourseHubDashboard.jsx and (once
// wired up) the signup/payment flow should import from here rather
// than each keeping their own copy of this mapping — that's exactly
// how the "every track shows every course" bug and the "Clean Code
// categorized as Frontend" bug happened in the first place: the
// same piece of knowledge lived nowhere in particular.
//
// Design decisions baked in here (confirmed by Hazzan):
//   1) Each paid track unlocks its own courses PLUS a fixed set of
//      "shared" categories that every student needs regardless of
//      track (Git/Linux, testing, clean code, design patterns).
//   2) A course whose category isn't claimed by any track yet
//      (Cloud, Systems, AI/LLM, Project-Guide) defaults to SHARED
//      rather than hidden. Hiding-by-default would silently lock
//      out content the moment someone adds a new course in a
//      category nobody thought to map — a fail-open default is the
//      safer mistake here. This is a placeholder until those
//      subjects get their own track in the AI mentor's PROFESSIONS
//      list (see AiCourseAssistancePage.jsx) — right now it only
//      offers 4 professions (frontend/backend/fullstack/database)
//      against ~10 actual course categories, so Cloud/Systems/AI/
//      Project-Guide have no track that could unlock them anyway.
// ============================================================

// Profession id (from AiCourseAssistancePage.jsx's PROFESSIONS) ->
// the ALL_COURSES `category` values it grants access to.
export const TRACK_CATEGORY_MAP = {
    frontend: ["Frontend", "Web"],
    backend: ["Backend"],
    fullstack: ["Frontend", "Web", "Backend"],
    database: ["Data"],
};

// Categories visible to every logged-in student, regardless of
// which track(s) they're enrolled in.
export const SHARED_CATEGORIES = [
    "DevOps", // Github, Linux, Docker, EmbeddedC-IoT, Software-Testing —
    // the last two were regrouped here from Systems/QA per Hazzan's
    // request; still shared either way, just a cleaner grouping.
    "QA", // No course currently uses this category (Software-Testing
    // moved to DevOps above) — left in place as a fail-open
    // placeholder in case a QA-specific course is added later.
    // NOTE (see module header, point 2): that fail-open promise only
    // holds for categories listed here explicitly; a brand-new
    // category that's in neither this list nor TRACK_CATEGORY_MAP
    // would be invisible to everyone, not shared by default.
    "Core Practices", // No course currently uses this category —
    // Clean Code and Design Patterns were moved OUT of here into
    // "Backend" per Hazzan's latest request, which means they are no
    // longer visible to frontend-only/database-only students, only
    // backend/fullstack (or All-Access). Left this category in place
    // as a placeholder for future universally-shared content.
    "Project-Guide", // No course currently uses this category —
    // SpringBoot Architecture was moved OUT of here into "Backend"
    // per Hazzan's latest request, same access-narrowing effect as
    // Clean Code/Design Patterns above.
    "Cloud", // Google Cloud — no dedicated track yet, see note above
    "Systems", // C++, System Design — no dedicated track yet, see
    // note above. (EmbeddedC-IoT moved out to DevOps, see above.)
    "AI", // AI & LLM — no dedicated track yet, see note above
];

/**
 * Can this user see this course in the Course Hub?
 *
 * @param {{category: string}} course - one entry from ALL_COURSES
 * @param {{enrolledTracks: string[], allAccess: boolean}} access - the
 *   shape returned by AuthHelpers.getUserAccess()
 */
export function canAccessCourse(course, access) {
    if (!access) return false;
    if (access.allAccess) return true;
    if (SHARED_CATEGORIES.includes(course.category)) return true;

    return (access.enrolledTracks || []).some((track) =>
        (TRACK_CATEGORY_MAP[track] || []).includes(course.category)
    );
}

/**
 * Which category chips should even be offered to this user? A
 * logged-in frontend student clicking a "Backend" filter chip that
 * then shows zero results is a worse experience than not showing
 * the chip at all.
 */
export function visibleCategoriesFor(allCourses, access) {
    const accessible = allCourses.filter((c) => canAccessCourse(c, access));
    return ["All", ...new Set(accessible.map((c) => c.category))];
}
